import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Avatar, FormControl, Grid, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { Color } from '../../../assets/color';
import { DeleteSvg, EditSvg, UploadImageSvg } from '../../../assets/svg';
import RouteHeader from '../../../components/common/RouteHeader';
import { HideDateFromCurrent, IndianRupee } from '../../../utils/common-function';
import DatatableHeading from '../../../components/heading/DatatableHeading';
import DatatableWithSearch from '../../../components/datatable/DatatableWithSearch';
import * as SuperadminActions from '../../../redux/actions/superadminAction';
import { api_urls } from '../../../utils/api-urls';

const Reports = () => {
    const [mode, setMode] = useState('');
    const dispatch = useDispatch();
    const { reportData, reportModulesData: reportModules } = useSelector(state => state?.superadminReducer);
    const [inputFieldDetail, setInputFieldDetail] = useState({ title: "", from_age: "", to_age: "", mrp_in_inr: "", mrp_in_dollor: "", offer_price_inr: "", offer_price_dollor: "", priority: "" });
    const [inputFieldError, setInputFieldError] = useState({});

    const handleInputFieldDetail = (event) => setInputFieldDetail({ ...inputFieldDetail, [event?.target?.name]: event?.target?.value });  //* Handle Input Field : Data
    const handleInputFieldDetailError = (input, value) => setInputFieldError((prev) => ({ ...prev, [input]: value })); //* Handle Input Field : Error

    //! Modules
    const [selectedModules, setSelectedModules] = useState([]);
    const handleModuleToggle = (key) => {
        setSelectedModules(prev =>
            prev.includes(key)
                ? prev.filter(m => m !== key)
                : [...prev, key]
        );
    };

    const [image, setImage] = useState({ file: '', bytes: '' });
    //! Handle Image : Normally
    const handleImage = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            setImage({
                file: URL.createObjectURL(e.target.files[0]),
                bytes: e.target.files[0],
            });
        }

        handleInputFieldDetailError("image", null);
    };

    //! Handle Image : Drop Feature
    const handleDrop = (e) => {
        e.preventDefault();
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            setImage({
                file: URL.createObjectURL(e.dataTransfer.files[0]),
                bytes: e.dataTransfer.files[0],
            });
        }

        handleInputFieldDetailError("image", null);
    };

    //* Handle Reset 
    const handleReset = () => {
        setInputFieldDetail({ title: "", from_age: "", to_age: "", mrp_in_inr: "", mrp_in_dollor: "", offer_price_inr: "", offer_price_dollor: "" });
        setSelectedModules([]);
        setImage({ file: '', bytes: '' });
        setMode('');
    };

    //* Handle Edit
    const handleEdit = (data) => {
        setInputFieldDetail({ ...data, start_date: moment.utc(data?.start_date).format('YYYY-MM-DD'), end_date: moment.utc(data?.end_date).format('YYYY-MM-DD'), id: data?._id });
        setSelectedModules(data?.modules || []);
        setImage({ file: api_urls + data?.image, bytes: '' });
        setMode('Edit');
    };

    //! Handle Submit : Create | Edit
    const handleSubmit = () => {
        const { title, from_age, to_age, mrp_in_inr, mrp_in_dollor, offer_price_inr, offer_price_dollor, priority } = inputFieldDetail;
        let formData = new FormData();

        formData.append('title', title);
        formData.append('from_age', from_age);
        formData.append('to_age', to_age);
        formData.append('mrp_in_inr', mrp_in_inr);
        formData.append('mrp_in_dollor', mrp_in_dollor);
        formData.append('offer_price_inr', offer_price_inr);
        formData.append('offer_price_dollor', offer_price_dollor);
        formData.append('priority', priority);
        formData.append("image", image?.bytes);
        selectedModules.forEach(m => formData.append("modules[]", m));

        const payload = {
            data: formData,
            onComplete: () => handleReset()
        };

        if (mode === "Edit") {
            payload.id = inputFieldDetail?.id;
            dispatch(SuperadminActions.updateReport(payload));
        } else {
            dispatch(SuperadminActions.createReport(payload));
        }
    };

    //* Datatable Columns
    const columns = [
        { name: 'S.No.', selector: row => reportData?.indexOf(row) + 1, width: '80px' },
        { name: "Title", selector: row => <div style={{ textTransform: 'capitalize' }}>{row?.title}</div>, width: '300px' },
        { name: "Priority", selector: row => row?.priority ?? 0, width: "120px" },
        { name: 'Image', cell: row => <div style={{ padding: '10px' }}><Avatar src={row?.image ? api_urls + row?.image : ''} alt="Image" style={{ width: '60px', height: '60px', borderRadius: '50%' }} /></div>, },
        { name: "Age Range", selector: row => `${row?.from_age} - ${row?.to_age}` },
        { name: "INR", selector: row => IndianRupee(row?.mrp_in_inr || 0) },
        { name: "Offer INR", selector: row => row?.offer_price_inr ? IndianRupee(row?.offer_price_inr) : '-' },
        { name: "USD", selector: row => `$${row?.mrp_in_dollor || 0}/-` },
        { name: "Offer USD", selector: row => row?.offer_price_dollor ? `$${row?.offer_price_dollor}/-` : '-' },
        {
            name: "Action",
            cell: row => (
                <div style={{ display: "flex", gap: "16px" }}>
                    <div onClick={() => handleEdit(row)} style={{ cursor: "pointer" }}><EditSvg /></div>
                    <div onClick={() => dispatch(SuperadminActions.deleteReport({ id: row?._id }))} style={{ cursor: "pointer" }}><DeleteSvg /></div>
                </div>
            )
        }
    ];

    useEffect(() => {
        //! Dispatching API
        dispatch(SuperadminActions.getReportModules());
        dispatch(SuperadminActions.getReports());
    }, []);

    return (
        <>
            <RouteHeader title="Reports" sub_title="Create and manage reports for your system." />

            <div style={{ backgroundColor: Color.white, padding: "20px", borderRadius: "10px" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <DatatableHeading title="Report Management" sub_title="Manage reports for your system. Only Super Admins can create, edit, or delete reports." />
                    <div onClick={() => setMode("Add")} style={{ fontSize: "14.5px", fontWeight: "500", padding: "10px 20px 9px 20px", borderRadius: "20px", background: "linear-gradient(to right,#6653E8,#9544EB)", color: "#FFF", cursor: "pointer" }}>{mode ? mode : "Add"} Report</div>
                </div>

                <Grid container spacing={3} sx={{ marginTop: '20px' }}>
                    {mode && <>
                        <Grid item lg={12} sm={12} md={12} xs={12} >
                            <div style={{ color: "#000", border: "1px solid #C4C4C4", borderRadius: "3px" }}>
                                {image?.file ?
                                    <label onDragOver={(e) => e.preventDefault()} onDrop={handleDrop} htmlFor="upload-image" style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "20px", cursor: "pointer" }}>
                                        <div style={{ height: '300px' }}>
                                            <Avatar src={image.file} style={{ height: '100%', width: '100%', borderRadius: "initial", objectFit: 'contain' }} />
                                        </div>
                                    </label>
                                    :
                                    <label onDragOver={(e) => e.preventDefault()} onDrop={handleDrop} htmlFor="upload-image" style={{ display: "flex", flexDirection: "column", gap: "20px", alignItems: "center", padding: "100px 0", cursor: "pointer" }}>
                                        <UploadImageSvg h="80" w="80" color="#C4C4C4" />
                                        <div style={{ fontWeight: "600", fontSize: "18px" }}>Choose Your Image to Upload <span style={{ color: "red" }}>*</span></div>
                                        <div style={{ fontWeight: "500", fontSize: "16px", color: 'grey' }}>Or Drop Your Image Here</div>
                                    </label>}
                                <input id="upload-image" onChange={handleImage} hidden accept="image/*" type="file" />
                            </div>
                            {inputFieldError?.image && <div style={{ color: "#D32F2F", fontSize: "12.5px", padding: "10px 0 0 12px", }}>{inputFieldError?.image}</div>}
                        </Grid>

                        <Grid item lg={3} xs={12}>
                            <TextField label="Report Title" name="title" fullWidth value={inputFieldDetail.title} onChange={handleInputFieldDetail} />
                        </Grid>

                        <Grid item lg={1.5} xs={12}>
                            <TextField label="From Age" name="from_age" type="number" fullWidth value={inputFieldDetail.from_age} onChange={handleInputFieldDetail} />
                        </Grid>

                        <Grid item lg={1.5} xs={12}>
                            <TextField label="To Age" name="to_age" type="number" fullWidth value={inputFieldDetail.to_age} onChange={handleInputFieldDetail} />
                        </Grid>

                        <Grid item lg={1.5} xs={12}>
                            <TextField label="Priority" name="priority" type="number" fullWidth value={inputFieldDetail.priority} onChange={handleInputFieldDetail} />
                        </Grid>

                        <Grid item lg={2.5} xs={12}>
                            <TextField label="Regular Price (INR)" name="mrp_in_inr" type="number" fullWidth value={inputFieldDetail.mrp_in_inr} onChange={handleInputFieldDetail} />
                        </Grid>

                        <Grid item lg={2.5} xs={12}>
                            <TextField label="Offer Price (INR)" name="offer_price_inr" type="number" fullWidth value={inputFieldDetail.offer_price_inr} onChange={handleInputFieldDetail} />
                        </Grid>

                        <Grid item lg={2} xs={12}>
                            <TextField label="Regular Price (Dollar)" name="mrp_in_dollor" type="number" fullWidth value={inputFieldDetail.mrp_in_dollor} onChange={handleInputFieldDetail} />
                        </Grid>

                        <Grid item lg={2} xs={12}>
                            <TextField label="Offer Price (Dollar)" name="offer_price_dollor" type="number" fullWidth value={inputFieldDetail.offer_price_dollor} onChange={handleInputFieldDetail} />
                        </Grid>

                        <Grid item lg={12} xs={12}>
                            <div style={{ border: "1px solid #d1d5db", borderRadius: 8, padding: 16 }}>
                                <div style={{ fontWeight: 600, marginBottom: 10 }}>Select Report Modules</div>
                                <Grid container spacing={2}>
                                    {reportModules?.map(mod => (
                                        <Grid item sm={6} xs={12} key={mod.key}>
                                            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: '15px' }}>
                                                <input type="checkbox" checked={selectedModules.includes(mod.key)} onChange={() => handleModuleToggle(mod.key)} />
                                                {mod.label}
                                            </label>
                                        </Grid>
                                    ))}
                                </Grid>
                            </div>
                        </Grid>

                        <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Grid container spacing={2} justifyContent="flex-end">
                                <Grid item>
                                    <div onClick={() => setMode('')} style={{ padding: '10px 20px', border: `1px solid ${Color.primary}`, borderRadius: '5px', backgroundColor: Color.white, color: Color.primary, fontSize: '14px', cursor: 'pointer' }}>Cancel</div>
                                </Grid>

                                <Grid item>
                                    <div onClick={handleSubmit} style={{ padding: '10px 20px', border: `1px solid ${Color.primary}`, borderRadius: '5px', background: "linear-gradient(to right,  #6653E8, #9544EB)", color: Color.white, fontSize: '14px', cursor: 'pointer' }}>{mode} Report</div>
                                </Grid>
                            </Grid>
                        </Grid>
                    </>}

                    <Grid item lg={12} md={12} sm={12} xs={12}>
                        <DatatableWithSearch data={reportData} columns={columns} />
                    </Grid>
                </Grid>
            </div>
        </>
    )
}

export default Reports;